<template>

<div class="list-view">

	<div class="sub-header">
		<div class="input-with-control">
			<input type="search" ref="search_term" v-model="filters.search_term" placeholder="Search..." @search="getList()" v-focus>
			<div class="button action input-go" @click="getList()"><i class="fas fa-search"></i></div>
		</div>
		<div class="controls">
			<pagination v-if="showPagination" :paging="paging" @updatePage="goToPage"></pagination>
		</div>
	</div>

	<div class="filters" v-if="showFilters">
		<div v-if="!showDetail">
			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.internal_use_only ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('internal_use_only')"></span>
					<label>Hidden</label>
				</li>
				<li>
					<span :class="['pointer', 'far', filters.inactive ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('inactive')"></span>
					<label>Inactive</label>
				</li>
			</ul>
			<div class="divider"></div>
			<div v-if="hasFilters" class="button neutral" @click="clearFilters()">Clear Filters</div>
			<div v-else class="button disabled">Clear Filters</div>
			<div class="button action" @click="addItem()">Add Range Bay</div>
			<div class="divider"></div>
			<div class="extra-menu-item" @click="showLaneSettings = true">Reservations Settings</div>
			<div class="extra-menu-item" @click="showRangeSettings = true">Website Settings</div>

			<div class="divider"></div>
			<p>Use the <i class="fas fa-grip-vertical"></i> handle to drag bays into preferred display order.</p>
			<button class="button neutral inline spaced" @click="saveBayDisplayOrder()"><span>Save Display Order</span></button>

		</div>
		<div v-if="showDetail">
			<div :class="['button', canSave ? 'action' : 'disabled']" @click="saveItem()">Save Range Bay Setup</div>
			<div class="button neutral" @click="backToList()">Back To List</div>
			<div class="message">{{ saveMessage }}</div>
		</div>

		<div v-if="enableExport" :class="['button', gettingExport ? 'disabled' : 'action']" @click="doExport()">Export CSV</div>
		<div class="message">{{ exportMessage }}</div>
	</div>

	<loading v-if="loading"></loading>

	<div v-else-if="showDetail" class="detail two-columns">
		<div class="section">
			<h2>UPC / Product Info</h2>
			<div class="note">Range time is a special type of product. Each range product should have it's own UPC, a default price, one or more lanes, time block size and schedule.</div>
			<div class="two-columns">
				<label-required :label="'UPC'" :condition="listDetail.product.upc" />
				<label>SKU</label>
				<input type="text" v-model="listDetail.product.upc"/>
				<input type="text" v-model="listDetail.product.sku"/>
			</div>
			<label-required :label="'Description'" :condition="listDetail.product.description" />
			<input type="text" v-model="listDetail.product.description"/>
			<div class="three-columns">
				<div class="checkbox-group">
					<span :class="['pointer', 'far', listDetail.product.members_only ? 'fa-check-square' : 'fa-square']" @click="listDetail.product.members_only = !listDetail.product.members_only"></span>
					<span class="label">Members only</span>
					<tool-tip content="When checked, only members can reserve time for this range product on your website."></tool-tip>
				</div>
				<div class="checkbox-group">
					<span :class="['pointer', 'far', listDetail.product.internal_use_only ? 'fa-check-square' : 'fa-square']" @click="listDetail.product.internal_use_only = !listDetail.product.internal_use_only"></span>
					<span class="label">Hidden Online</span>
					<tool-tip content="When checked, this range product will not be available on your website."></tool-tip>
				</div>
				<div class="checkbox-group">
					<span :class="['pointer', 'far', listDetail.product.inactive ? 'fa-check-square' : 'fa-square']" @click="listDetail.product.inactive = !listDetail.product.inactive"></span>
					<span class="label">Inactive</span>
					<tool-tip content="When checked, this range product will not be available on your website or in POS."></tool-tip>
				</div>
			</div>
			<div class="checkbox-group">
				<span :class="['pointer', 'far', listDetail.product.not_taxable ? 'fa-check-square' : 'fa-square']" @click="listDetail.product.not_taxable = !listDetail.product.not_taxable"></span>
				<span class="label">Range time is not taxable</span>
				<tool-tip content="When checked, sales tax will not be applied to this range product."></tool-tip>
			</div>
		</div>
		<div class="section">
			<h2>Reservations / Pricing</h2>
			<div style="padding: 0 0 0.5em 0;">
				All lanes are <b>{{ listDetail.range_lanes_open ? 'assigned in person' : 'reserved by calendar' }}</b>.
			</div>
			<div class="two-columns">
				<div>
					<label-required v-if="listDetail.range_lanes_open" :label="'Price Per Lane'" :condition="listDetail.pos_price" />
					<label-required v-else :label="'Price Per Block'" :condition="listDetail.pos_price" />
					<input type="text" v-model="listDetail.pos_price"/>
					<label-required v-if="!listDetail.range_lanes_open" :label="'Price Per Lane - Online'" :condition="listDetail.web_price" />
					<input v-if="!listDetail.range_lanes_open" type="text" v-model="listDetail.web_price"/>
					<label>Cost Per {{ listDetail.range_lanes_open ? 'Lane' : 'Block' }} (used for reporting)</label>
					<input type="text" v-model="listDetail.cost_price"/>
					<label-required :label="listDetail.range_lanes_open ? 'Default Block' : 'Block Size'" :condition="listDetail.block_size" />
					<select name='block_size' v-model="listDetail.block_size">
						<option value=''>Select...</option>
						<option value='15'>15 Minutes</option>
						<option value='30'>30 Minutes</option>
						<option value='60'>1 Hour</option>
						<option value='90'>90 Minutes</option>
						<option value='120'>Two Hours</option>
					</select>
				</div>
				<div style="padding: 1em 0 0 0;">
					<div v-if="listDetail.range_lanes_open">
						<!-- <p><b>Assigned in Person</b> allows cashiers or range managers to assign lanes to customers as they arrive. Lanes become available after a customer checks out.</p> -->
						<p><b>Price Per Lane</b> can be used as a flat fee or to calculate optional price for actual time in POS.</p>
						<p><b>Default Block</b> is used to calculate optional price for actual time in POS.</p>
						<p v-if="listDetail.pos_price && listDetail.block_size"><b>Optional Price for Actual Time</b><br>${{ listDetail.pos_price }} / {{ listDetail.block_size }} = ${{ Math.round(((listDetail.pos_price / listDetail.block_size) + Number.EPSILON) * 100) / 100 }} per minute</p>
					</div>
					<div v-else>
						<!-- <p><b>Reserve By Block</b> allows in-store customers or website visitors to purchase specific blocks of time as available or in advance.</p> -->
						<p><b>Price Per Block</b> is the unit price for each block reserved.</p>
						<p><b>Block Size</b> determines how much time is reserved on the calendar.</p>
						<p><b>Optional Price for Actual Time</b> can be selected at POS.</p>
					</div>
				</div>
			</div>
		</div>
		<div class="section">
			<h2>Lane Definitions</h2>
			<div class="note">Describe each lane available with this range product UPC (bay) and at this price.</div>
			<div v-for="(lane, index) in listDetail.lanes" class="input-with-control-double" style="grid-column-gap: 0.5em;" >
				<input type="text" v-model="lane.description" :class="{'input-grey': lane.internal_use_only}">
				<div>
					<div class="button neutral" @click="hideLane(index)" title="Set lane availability"><i :class="['fa', lane.internal_use_only ? 'fa-play' : 'fa-pause']"></i></div>
				</div>
				<div>
					<div class="button neutral" @click="removeLane(index)" title="Permanently remove / deactivate lane"><i class="fa fa-trash"></i></div>
				</div>
			</div>
			<div class="two-columns">
				<div>
					<label-required :condition="this.listDetail.lanes.length > 0" />
					<button class="action" @click="addLane()">Add A Lane</button>
				</div>
				<!-- <button class="grey" @click="syncLanes()">Sync Action Target</button> -->
			</div>
		</div>
		<div class="section">
			<h2>Schedule</h2>
			<div v-if="listDetail.range_lanes_open" class="note">Set opening and closing times for each day.</div>
			<div v-else class="note">Set start times of opening and closing blocks. Closing time will be last block start time plus block size.</div>
			<div v-for="(hour, index) in listDetail.hours" class="three-columns">
				<div class="checkbox-group">
					<span :class="['pointer', 'far', hour.enabled ? 'fa-check-square' : 'fa-square']" @click="hour.enabled = !hour.enabled"></span>
					<span class="label">{{ hour.label }}</span>
				</div>
				<select v-model="hour.start_time">
					<option v-for="(locationHour, index) in listDetail.location_hours" :value="locationHour.hour">{{ locationHour.label }}</option>
				</select>
				<select v-model="hour.end_time">
					<option v-for="(locationHour, index) in listDetail.location_hours" :value="locationHour.hour">{{ locationHour.label }}</option>
				</select>
			</div>
		</div>

	</div>

	<div v-else class="list">

	    <div v-if="list.length == 0" class="empty-list">
	        This list is empty
	    </div>

		<draggable class="distributor-items" handle=".drag" :sort="true" v-model="list" @end="endDrag" group="range-bay-items" tag="div">
			<div v-for="(item, index) in list" class="range-bay-item draggable" :key="'bay_' + item.product_id" @click="getItem(item)">
				<div>
					<div>
						<label>UPC</label>
						{{ item.upc }}
					</div>
					<div>
						<label>SKU</label>
						{{ item.sku }}
					</div>
				</div>
				<div>{{ item.description }}</div>
				<div class="left">
					<div>
						<i class="fa fa-check" :style="'padding: 0 0.5em 0 0; color: ' + (item.internal_use_only ? 'black;' : 'white;')"></i>
						<label>Hidden</label>
					</div>
					<div>
						<i class="fa fa-check" :style="'padding: 0 0.5em 0 0; color: ' + (item.inactive ? 'black;' : 'white;')"></i>
						<label>Inactive</label>
					</div>
				</div>
				<div class="drag right"><i class="fas fa-grip-vertical"></i></div>
			</div>
		</draggable>

	</div>

	<iframe id="download" src="" style="display: none;"></iframe>

	<div class="modal" v-if="showModal">
		<div class="inner">
			<h2>{{ modalTitle }}</h2>
			<div class="content">
				<div v-if="modalLoading" class="loading"><img src="@/assets/img/flightdeck-logo-inverse.png"></div>
				<div v-if="setActionTargetCredentials" class="two-columns">
					<label>Username</label>
					<input type="text" v-model="actionTargetCredentails.action_target_username">
					<label>Password</label>
					<input type="text" v-model="actionTargetCredentails.action_target_password">
					<label>Server IP Address</label>
					<input type="text" v-model="actionTargetCredentails.action_target_ip_address">
				</div>
				<table v-if="listActionTargetBays" class="clickable-list" width="100%">
					<tr v-for="(bay, index) in actionTargetBays" @click="getActionTargetLanes(bay)"><td class="clickable-list-item">{{ bay.name }}</td></tr>
				</table>
				<table v-if="listActionTargetLanes" class="clickable-list" width="100%">
					<tr v-for="(lane, index) in actionTargetLanes">
						<td>Lane {{ lane.name }}</td><td>{{ lane.retrieverId }}</td><td></td>
					</tr>
				</table>
				<div v-if="modalMessage" class="message">{{ modalMessage }}</div>
			</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showModal = false">Cancel</div>
				<div v-if="!setActionTargetCredentials" class="button action spaced" @click="setActionTargetCredentials = true; listActionTargetBays = false; listActionTargetLanes = false;">Credentails</div>
				<div v-if="setActionTargetCredentials" class="button action spaced" @click="saveActionTargetCredentails()">Save</div>
				<div v-if="listActionTargetLanes" class="button action spaced" @click="setActionTargetLanes()">Sync Lanes</div>
			</div>
		</div>
	</div>

	<div class="modal range" v-if="showRangeSettings">
		<div class="inner">
			<h2>Website Settings</h2>
			<div class="content">
				<div class="two-columns">
					<div>
						<label>Members Only?</label>
						<binary-toggle v-model="settings.range_calendar_members_only" labels="Yes,No" values="1," class="v-spaced" />
						<div class="note left">If 'Yes', only logged in members may reserve range time.</div>
					</div>
					<div>
						<label>Hide Calendar?</label>
						<binary-toggle v-model="settings.range_calendar_hidden" labels="Yes,No" values="1," class="v-spaced" />
						<div class="note left">If 'Yes', range calendar to be hidden from website.</div>
					</div>
				</div>
				<label>Calendar Description</label>
				<textarea v-model="settings.range_calendar_description" style="height: 7.5em;"></textarea>
				<div class="note left">Text to be displayed at the top of your website's range calendar.</div>
				<label>Additional Range Information</label>
				<ckeditor :editor="editor" v-model="settings.range_calendar_info" :config="editorConfig"></ckeditor>
				<div class="sm-spacer"></div>
				<div class="note left">Text to be displayed before your website's range calendar.</div>
			</div>
			<div v-if="saveMessage" class="note center">{{ saveMessage }}</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showRangeSettings = false">Cancel</div>
				<div :class="['button', saving ? 'disabled' : 'action', 'spaced']" @click="saveRangeSettings()">Save Settings</div>
			</div>
		</div>
	</div>

	<div class="modal" v-if="showLaneSettings">
		<div class="inner">
			<h2>Reservation Settings</h2>
			<div class="content">
				<p>How are lanes assigned?</p>
				<div class="two-columns">
					<p><checkbox v-model="reserveLanes" label="Time Reservations" @input="setOpenLanes('reserved')" :key="'reserve_' + reserveLanes" /></p>
					<p><checkbox v-model="openLanes" label="Open Lanes" @input="setOpenLanes('open')" :key="'reserve_' + openLanes"/></p>
				</div>
				<div class="two-columns" style="margin: 1em 0 0 0;">
					<p><b>Time Reservations</b> allow your customers to reserve specific blocks of time on a lane. This can be done in-store or on your website, and time blocks can be reserved in real-time or in advance.</p>
					<p><b>Open Lanes</b> allow you to assign customers to any open lane, and lanes only become available after the customer checks out. Open lanes can't be reserved in advanced, they are first-come first-served.</p>
				</div>
			</div>
			<div v-if="saveMessage" class="note center">{{ saveMessage }}</div>
			<div class="controls">
				<div class="button cancel spaced" @click="showLaneSettings = false">Cancel</div>
				<div :class="['button', saving ? 'disabled' : 'action', 'spaced']" @click="saveRangeSettings()">Save Settings</div>
			</div>
		</div>
	</div>
	
	<help-popup :helpLink="{404:'Range Bay Setup', 405:'Range Setup - Time Reservations', 406:'Range Setup - Open Lanes', 63:'Online Range Reservation', 408:'POS Range Time - Time Reservations', 407:'POS Range Time - Open Lanes'}" :tours="false"></help-popup>
	
	<message-modal v-if="message" :message="message" :loading="saving" :errorStatus="messageStatus" :timeout="messageTimeout" @dismiss="message = ''" />

</div>

</template>

<script>
import HelpPopup from '@/components/HelpPopup.vue'
import Checkbox from '@/components/Checkbox.vue'
import BinaryToggle from '@/components/BinaryToggle.vue'
import LabelRequired from '@/components/LabelRequired.vue'

import CKEditor from '@ckeditor/ckeditor5-vue2';
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic';
import Font from '@ckeditor/ckeditor5-font/src/font';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline';
import Link from '@ckeditor/ckeditor5-link/src/link';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import List from '@ckeditor/ckeditor5-list/src/list';
import Heading from '@ckeditor/ckeditor5-heading/src/heading';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment';
import { Undo, Redo } from '@ckeditor/ckeditor5-undo/src/';

export default {
	name: 'inventory-reason-codes',
	props: ['product_id'],
	components: {
		'binary-toggle': BinaryToggle,
		'checkbox': Checkbox,
		'label-required': LabelRequired,
		'help-popup': HelpPopup,
        'ckeditor': CKEditor.component
    },
	data() {
		return {
			client_id: '',
            loading: false,
            message: '',
            messageStatus: '',
            messageTimeout: 0,
			searchTip: '',
			page: 1,
			showPagination: false,
			showFilters: true,
			filters: {},
			filterMessage: '',
			definition: [],
			list: [],
			paging: {},
			showDetail: false,
			listDetail: {},
			enableExport: false,
			exportMessage: '',
			gettingExport: false,
			saveMessage: '',
			showModal: false,
			modalTitle: '',
			modalLoading: false,
			modalMessage: '',
			actionTargetCredentails: {
				action_target_ip_address: '',
				action_target_username: '',
				action_target_password: ''
			},
			setActionTargetCredentials: false,
			listActionTargetBays: false,
			actionTargetBays: [],
			listActionTargetLanes: false,
			actionTargetLanes: [],
			showRangeSettings: false,
			showLaneSettings: false,
			settings: {},
			openLanes: false,
			reserveLanes: false,
			saving: false,
			editor: ClassicEditor,
			editorConfig: {
                plugins: [
					Alignment,
                    Bold,
					Font,
                    Italic,
					Underline,
                    Link,
                    Paragraph,
					Heading,
					List,
					Undo,
                ],
                toolbar: {
                    items: [
						'bold',
                        'italic',
						'underline',
						'fontSize',
						'heading',
						'link',
						'alignment:left',
						'alignment:center',
						'alignment:right',
						'numberedList',
						'bulletedList',
                        'undo',
                    ]
                },
				heading: {
		            options: [
		                { model: 'paragraph', title: 'Paragraph' },
		                { model: 'heading1', view: 'h1', title: 'Heading 1' },
						{ model: 'heading2', view: 'h2', title: 'Heading 2' },
		                { model: 'heading3', view: 'h3', title: 'Heading 3' },
					]
				},
				fontSize: {
    		    	options: [ 12, 'default', 24, 32 ]
        		},
				link: {
					decorators: {
						openInNewTab: {
							mode: 'manual',
							label: 'Open in new tab',
							attributes: {
								target: '_blank',
								rel: 'noopener noreferrer' // prevent reverse tabnabbing
							}
						}
					}
				}
            },
		}
	},
	created() {
		this.clearFilters()
	},
	computed: {
		canSave() {
			if (this.listDetail.lanes.length === 0) { return false}
			// check if any empty description
  			let emptyDescription = this.listDetail.lanes.some((lane) => lane.description.length === 0);
			return this.client_id == this.listDetail.product.client_id && this.listDetail.product.upc && this.listDetail.product.description && this.listDetail.pos_price && this.listDetail.block_size && !emptyDescription
		},
		hasFilters() {
			return this.filters.search_term || this.filters.internal_use_only || this.filters.inactive
		}
	},
	watch: {
	},
	methods: {
		setOpenLanes(which) {
			if (which == 'open') {
				this.settings.range_lanes_open = 1
				this.reserveLanes = false
			} else {
				this.settings.range_lanes_open = 0
				this.openLanes = false
			}
		},
		clearFilters() {
			this.filters = {
				page: 1,
				search_term: '',
				internal_use_only: 0,
				inactive: 0,
				do_export: 0
			}
			if (this.product_id) {
				this.getItem({ product_id: this.product_id })
			} else {
				this.getList()
			}
		},
		getList() {
			if (this.loading) { return false }
			this.loading = true
			this.apiCall({ destination: 'get_list_range_setup', data: this.filters }).then(obj => {
				this.client_id = obj.client_id
				this.loading = false
				this.showDetail = false
				if (this.filters.do_export) {
					this.filters.do_export = 0
					var outputFile = obj.file_name
					// accommodate live server filecache latency
					setTimeout(() => {
						document.getElementById("download").src = "//" + document.location.hostname + "/download.php?file_name=" + obj.file_name + "&output_file_name=" + outputFile
						this.gettingExport = false
						this.exportMessage = ''
					}, 1500)
				} else {
					this.list = obj.list
					if (obj.settings) {
						this.settings = obj.settings
						if (this.settings.range_lanes_open) {
							this.openLanes = true
						} else {
							this.reserveLanes = true
						}
					}
					if (obj.paging) {
						this.paging = obj.paging
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
			this.getList()
		},
		endDrag() {
			let sortedList = []
			sortedList = this.list.map((item, index) => {
				item.sort_order = index + 1
				return item
			})
			this.list = sortedList
		},
		saveBayDisplayOrder() {
			if (this.saving) { return }
			this.saving = true
			this.message = 'Saving range setup...'
			this.messageStatus = ''
			this.messageTimeout = 0
			let bayOrder = []
			// iterate the list and add the key distributor_id value sort_order to the priorityList
			this.list.forEach(item => {
				bayOrder.push(item.product_id)
			})
			this.apiCall({ destination: 'save_item_range_bay_order', data: { bay_order: bayOrder } }).then(obj => {
				this.messageStatus = obj.status
				if (obj.status == 'success') {
					this.message = 'Bay order saved!'
					this.messageTimeout = 2000
					this.saving = false
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		saveRangeSettings() {
			if (this.saving) { return false }
			this.saving = true
			this.apiCall({ destination: 'save_preference_set', data: { preference_set: this.settings } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.saveMessage = 'Settings saved!'
					setTimeout(() => {
						this.saveMessage = ''
						this.showLaneSettings = false
						this.showRangeSettings = false
					}, 1500)
				} else {
					this.saveMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getItem(item) {
			if (this.loading) { return false }
			if (!this.product_id) {
				this.$router.push({ path: '/range-memberships/range-setup/' + item.product_id })
			}
			this.loading = true
			this.saveMessage = ''
			this.apiCall({ destination: 'get_item_range_setup', data: { product_id: item.product_id } }).then(obj => {
				this.loading = false
				if (obj.status == 'success') {
					this.client_id = obj.client_id
					this.listDetail = obj.detail
					this.showDetail = true
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		addLane() {
			this.listDetail.lanes.push({'description': ''})
		},
		hideLane(index) {
			if (this.listDetail.lanes[index].internal_use_only == 1) {
				this.listDetail.lanes[index].internal_use_only = 0
				//this.listDetail.lanes[index].description = this.listDetail.lanes[index].description.replace(" - Unavailable", "")
			} else {
				this.listDetail.lanes[index].internal_use_only = 1
				//this.listDetail.lanes[index].description += ' - Unavailable'
			}
		},
		removeLane(index) {
			this.listDetail.lanes.splice(index, 1)
		},
		syncLanes() {
			this.modalTitle = 'Checking Action Target credentials...'
			this.modalLoading = true
			this.showModal = true
			if (this.actionTargetCredentails.action_target_username && this.actionTargetCredentails.action_target_password) {
				this.getActionTargetBays()
			} else {
				this.apiCall({ destination: 'get_action_target_credentials' }).then(obj => {
					this.modalLoading = false
					if (obj.status == 'success') {
						if (obj.credentials.action_target_username && obj.credentials.action_target_password) {
						 	this.actionTargetCredentails = obj.credentials
							this.getActionTargetBays()
						} else {
							this.modalTitle = 'Your Action Target credentials...'
							/////////////////////////////////////////////////////
							// TESTING ONLY!
							// this.actionTargetCredentails.action_target_ip_address = "3.15.152.150"
							// this.actionTargetCredentails.action_target_username = "pos"
							// this.actionTargetCredentails.action_target_password = "2L9eQ^Wn\\dtyvAKm"
							/////////////////////////////////////////////////////
							this.setActionTargetCredentials = true
						}
					} else {
						this.modalMessage = obj.message
					}
				}).catch(err => {
					console.log(err)
				})
			}
		},
		saveActionTargetCredentails() {
			if (this.actionTargetCredentails.action_target_username && this.actionTargetCredentails.action_target_password && this.actionTargetCredentails.action_target_ip_address) {
				this.apiCall({ destination: 'save_action_target_credentials', data: this.actionTargetCredentails }).then(obj => {
					if (obj.status == 'success') {
						this.setActionTargetCredentials = false
						this.getActionTargetBays()
					} else {
						this.modalMessage = obj.message
					}
				}).catch(err => {
					console.log(err)
				})
			}
		},
		getActionTargetBays() {
			this.setActionTargetCredentials = false
			this.modalTitle = 'Getting Action Target bays...'
			this.modalLoading = true
			this.apiCall({ destination: 'get_action_target_bays', data: this.actionTargetCredentails }).then(obj => {
				this.modalLoading = false
				if (obj.status == 'success') {
					this.modalTitle = 'Select a bay to sync with this UPC...'
					this.listActionTargetBays = true
					this.actionTargetBays = obj.bays
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getActionTargetLanes(bay) {
			this.actionTargetCredentails.action_target_bay_id = bay.id
			this.listActionTargetBays = false
			this.modalTitle = 'Getting Action Target lanes for ' + bay.name + '...'
			this.modalLoading = true
			this.apiCall({ destination: 'get_action_target_lanes', data: this.actionTargetCredentails }).then(obj => {
				this.modalLoading = false
				if (obj.status == 'success') {
					this.modalTitle = 'Action Target lanes in ' + bay.name + '...'
					this.listActionTargetLanes = true
					this.actionTargetLanes = obj.lanes
				} else {
					this.modalMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setActionTargetLanes() {
			let newLanes = []
			for (let i = 0; i < this.actionTargetLanes.length; i++) {
				// does this lane name already exist?
				let rangeLaneId = ''
				for (let q = 0; q < this.listDetail.lanes.length; q++) {
					if (this.listDetail.lanes[q].description == 'Lane ' + this.actionTargetLanes[i].name) {
						rangeLaneId = this.listDetail.lanes[q].range_lane_id
						break
					}
				}
				newLanes.push({description: 'Lane ' + this.actionTargetLanes[i].name, lane_identifier: this.actionTargetLanes[i].id, range_lane_id: rangeLaneId})
			}
			this.listDetail.lanes = newLanes
			this.listActionTargetLanes = false
			this.actionTargetLanes = []
			this.modalTitle = ''
			this.showModal = false
		},
		addItem() {
			let item = { product_id: 'new' }
			this.getItem(item)
		},
		saveItem() {
			if (this.saving) { return }
			if (!this.canSave) { return }
			// any checked hours must have start and end time
			for (const [key, row] of Object.entries(this.listDetail.hours)) {
				if (row.enabled && (!row.start_time || !row.end_time)) {
					this.message = 'Please select start and end times for each checked day.'
					this.messageStatus = 'invalid'
					return
				}
			}
			this.saving = true
			this.message = 'Saving range bay setup...'
			this.messageStatus = ''
			this.messageTimeout = 0
			this.apiCall({ destination: 'save_item_range_setup', data: this.listDetail }).then(obj => {
				this.messageStatus = obj.status
				this.saving = false
				if (obj.status == 'success') {
					this.message = 'Range bay setup saved!'
					this.messageTimeout = 1500
					if (this.product_id == 'new') {
						this.$router.push({ path: '/range-memberships/range-setup/' + obj.product_id })
					}
				} else {
					this.saveMessage = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		backToList() {
			this.$router.push({ path: '/range-memberships/range-setup' })
			this.getList()
			this.listDetail = {}
			this.showDetail = false
		},
		doExport() {
			if (this.gettingExport) {
				return false
			}
			this.gettingExport = true
			this.exportMessage = 'Generating CSV for ' + this.list.length + ' items...'
			this.filters.product_id = ''
			this.filters.do_export = true
			this.getList()
		}
	}
}
</script>
<style lang="scss" scoped>
.modal.range {
	.content {
		max-height: 60vh;
		textarea {
			resize: none;
		}
	}
	.inner {
		max-height: 80vh;
    	max-width: 70vw;
	}
	.sm-spacer {
		margin: .75rem;
	}
}
.draggable {
	.drag {
		cursor: move;
	}
}
.template-content .sortable-ghost {
	border: 1px solid gray;
	min-height: 2em;
}
.sortable-drag {
	cursor: grabbing;
	opacity: 0.5;
}
.sortable-chosen {
	cursor: grabbing;
	color: red;
	opacity: 0.5;
}
.range-bay-item {
	cursor: pointer;
	background-color: white;
	border: 1px solid #ccc;
	padding: 1em;
	margin: 1em;
	display: grid;
	grid-template-columns: 14em 1fr 10em 6em;
	align-items: center;
}
</style>
